
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { User, Loader2, Trash2 } from "lucide-react";
import type { Feedback } from "@/types/feedback";
import { getStatusBadgeColor, getTypeBadgeColor } from "@/utils/feedbackUtils";

interface FeedbackCardProps {
  feedback: Feedback;
  processingId: string | null;
  onStatusUpdate: (id: string, newStatus: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

export function FeedbackCard({ feedback, processingId, onStatusUpdate, onDelete }: FeedbackCardProps) {
  return (
    <Card key={feedback.id} className="overflow-hidden">
      <CardContent className="p-6">
        <div className="flex flex-col space-y-4">
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <div className="flex gap-2 items-center">
              <Badge variant="secondary" className={getTypeBadgeColor(feedback.type)}>
                {feedback.type.replace('_', ' ')}
              </Badge>
              <Badge variant="secondary" className={getStatusBadgeColor(feedback.status)}>
                {feedback.status.replace('_', ' ')}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {new Date(feedback.created_at).toLocaleDateString()}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => onDelete(feedback.id)}
                disabled={!!processingId}
              >
                {processingId === feedback.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          <div className="flex items-start gap-2 text-sm text-muted-foreground">
            <User className="h-4 w-4" />
            <div>
              <span className="font-medium">{feedback.user?.username || 'Anonymous'}</span>
              {feedback.user?.email && (
                <span className="ml-2">({feedback.user.email})</span>
              )}
            </div>
          </div>

          <p className="text-lg">{feedback.content}</p>

          {feedback.type === 'airport_request' && (
            <div className="bg-muted p-4 rounded-md space-y-2">
              <p><strong>IATA:</strong> {feedback.airport_iata || 'Not provided'}</p>
              <p><strong>Name:</strong> {feedback.airport_name || 'Not provided'}</p>
              <p><strong>Location:</strong> {feedback.airport_location || 'Not provided'}</p>
            </div>
          )}

          {feedback.admin_notes && (
            <div className="bg-muted p-4 rounded-md">
              <p><strong>Admin Notes:</strong> {feedback.admin_notes}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {feedback.status === 'pending' && (
              <>
                <Button
                  variant="default"
                  onClick={() => onStatusUpdate(feedback.id, 'in_progress')}
                  disabled={!!processingId}
                >
                  {processingId === feedback.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Start Processing
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => onStatusUpdate(feedback.id, 'rejected')}
                  disabled={!!processingId}
                >
                  {processingId === feedback.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Reject
                </Button>
              </>
            )}

            {feedback.status === 'in_progress' && (
              <Button
                variant="default"
                onClick={() => onStatusUpdate(feedback.id, 'resolved')}
                disabled={!!processingId}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                {processingId === feedback.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Mark as Resolved
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
